import type { ExpandedState, Row } from "@tanstack/react-table"
import { getCoreRowModel } from "@tanstack/react-table"
import { type FC, type ReactNode, useState } from "react"

import { useCostBreakdownPurchaseOrderColumns } from "../../hooks"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { GoodsReceipt } from "@/graphql/codegen/graphql"
import { ProductCostBreakdownDetail } from "@/screens/Procurement/CostBreakdown/components/ProductCostBreakdownDetail"
import type { PurchaseOrderLineWithReceiptContext } from "@/screens/WarehouseReceiving/types"
import { getGoodsReceiptPurchaseOrderLines } from "@/screens/WarehouseReceiving/utils"

interface CostBreakdownPurchaseOrdersTableProps {
  goodsReceipt: GoodsReceipt
  purchaseOrderLineId?: string
  canExpandIncompleteRows?: boolean
  canMaintain?: boolean
  renderExpandedContent?: (row: Row<PurchaseOrderLineWithReceiptContext>) => ReactNode
  onProductCostEdit?: (goodsReceipt: GoodsReceipt, purchaseOrderLineId: string) => void
}

export const CostBreakdownPurchaseOrdersTable: FC<CostBreakdownPurchaseOrdersTableProps> = ({
  goodsReceipt,
  purchaseOrderLineId,
  canExpandIncompleteRows = false,
  canMaintain = false,
  renderExpandedContent: customRenderExpandedContent,
  onProductCostEdit,
}) => {
  const purchaseOrderLines = getGoodsReceiptPurchaseOrderLines(goodsReceipt)
  const [expandedState, setExpandedState] = useState<ExpandedState>(() => {
    if (!purchaseOrderLineId) {
      return {}
    }

    const rowIndex = purchaseOrderLines.findIndex((line) => line.orderLineId === purchaseOrderLineId)

    return rowIndex === -1 ? {} : { [rowIndex]: true }
  })

  const columns = useCostBreakdownPurchaseOrderColumns({
    goodsReceipt,
    canExpandIncompleteRows,
    canMaintain,
    onProductCostEdit,
  })

  const isRowCompleted = (purchaseOrderLine: PurchaseOrderLineWithReceiptContext) => {
    return purchaseOrderLine.costBreakdown?.isCompleted || false
  }

  const defaultRenderExpandedContent = (row: Row<PurchaseOrderLineWithReceiptContext>) => {
    if (!row.original.costBreakdown) return null

    return <ProductCostBreakdownDetail lineCostBreakdown={row.original.costBreakdown} />
  }

  const renderExpandedContent = customRenderExpandedContent || defaultRenderExpandedContent

  return (
    <AerosBaseTable<PurchaseOrderLineWithReceiptContext>
      slotProps={{
        headerCell: { className: "bg-primary-100" },
        bodyCell: { className: "px-4" },
        bodyCellExpanded: { className: "bg-primary-100" },
      }}
      features={{
        tanstackOptions: {
          data: purchaseOrderLines,
          columns,
          enableSorting: false,
          getCoreRowModel: getCoreRowModel(),
          state: {
            expanded: expandedState,
          },
          onExpandedChange: setExpandedState,
          getRowCanExpand: (row) => (canExpandIncompleteRows ? true : isRowCompleted(row.original)),
          renderExpandedContent,
        },
      }}
      noDataMessage='No purchase orders found'
    />
  )
}
